import * as React from 'react'
import { ChartScanViolations } from '@/lib/api/company/chartScan'
import { DAY_NOTES_MAX_LENGTH } from '@/lib/api/company/driverDay/api'
import { useChartT } from '../i18n'

// Violation labels and grouping match the WPF resx (SuperTacho.Localization
// Resources.resx) and the layout in TachographChartImportView.xaml:174-253.
// The "(7.11) Nieumieszczenie..." header introduces 8 indented sub-checkboxes;
// the "(7.12) Wykresówka nie zawiera..." header introduces 3 sub-checkboxes.
type Row =
  | {
      kind: 'check'
      key: keyof ChartScanViolations
      tKey: string
      label: string
      indent?: boolean
    }
  | { kind: 'header'; tKey: string; label: string }

const ROWS: Row[] = [
  { kind: 'check', key: 'switchViolation', tKey: 'v73', label: '(7.3) Niepoprawne operowanie przełącznikiem tachografu umożliwiającym zmianę rodzaju aktywności kierowcy' },
  { kind: 'check', key: 'multipleChartsUsed', tKey: 'v77', label: '(7.7) Nieuzasadnione użycie kilku wykresówek w ciągu tego samego 24-godzinnego okresu' },
  { kind: 'check', key: 'dayExceededViolation', tKey: 'v78', label: '(7.8) Używanie wykresówki powyżej okresu, na jaki jest przeznaczona, wraz z utratą danych' },
  { kind: 'check', key: 'notSupportedChart', tKey: 'v79', label: '(7.9) Zastosowanie wykresówki nieposiadającej świadectwa homologacji typu lub nieprzeznaczonej dla danego typu tachografu' },
  { kind: 'check', key: 'dirtyChart', tKey: 'v710', label: '(7.10) Brudna lub uszkodzona wykresówka z nieczytelnymi danymi' },
  { kind: 'header', tKey: 'v711', label: '(7.11) Nieumieszczenie na wykresówce lub wydruku wymaganych wpisów lub umieszczenie na wykresówce lub wydruku nieczytelnych wpisów ręcznych' },
  { kind: 'check', key: 'nameOrSurnameViolation', tKey: 'v711_1', label: '1. Brak imienia lub nazwiska kierowcy', indent: true },
  { kind: 'check', key: 'registrationNumberViolation', tKey: 'v711_2', label: '2. Brak numeru rejestracyjnego pojazdu', indent: true },
  { kind: 'check', key: 'startPlaceViolation', tKey: 'v711_3', label: '3. Brak miejsca rozpoczęcia', indent: true },
  { kind: 'check', key: 'endPlaceViolation', tKey: 'v711_4', label: '4. Brak miejsca zakończenia', indent: true },
  { kind: 'check', key: 'startDateViolation', tKey: 'v711_5', label: '5. Brak daty rozpoczęcia', indent: true },
  { kind: 'check', key: 'endDateViolation', tKey: 'v711_6', label: '6. Brak daty zakończenia', indent: true },
  { kind: 'check', key: 'odometerStartViolation', tKey: 'v711_7', label: '7. Brak początkowego stanu licznika', indent: true },
  { kind: 'check', key: 'odometerEndViolation', tKey: 'v711_8', label: '8. Brak końcowego stanu licznika', indent: true },
  { kind: 'header', tKey: 'v712', label: '(7.12) Wykresówka nie zawiera wprowadzonych ręcznie bądź automatycznie wszystkich wymaganych danych' },
  { kind: 'check', key: 'missingActivitiesViolation', tKey: 'v712_1', label: '1. Brak wpisów aktywności', indent: true },
  { kind: 'check', key: 'beginCountryViolation', tKey: 'v712_2', label: '2. Brak wpisu kraju rozpoczęcia pracy', indent: true },
  { kind: 'check', key: 'endCountryViolation', tKey: 'v712_3', label: '3. Brak wpisu kraju zakończenia pracy', indent: true },
  { kind: 'check', key: 'timeMismatchViolation', tKey: 'v714', label: '(7.14) Niezgodność oznaczenia czasowego na wykresówce z urzędowym czasem kraju rejestracji pojazdu' },
]

const CHECK_KEYS: (keyof ChartScanViolations)[] = ROWS
  .filter((r): r is Extract<Row, { kind: 'check' }> => r.kind === 'check')
  .map((r) => r.key)

type Props = {
  violations: ChartScanViolations
  dayNote: string
  onChangeViolation: (key: keyof ChartScanViolations, value: boolean) => void
  onChangeDayNote: (note: string) => void
}

export function ViolationsExpander({
  violations,
  dayNote,
  onChangeViolation,
  onChangeDayNote,
}: Props) {
  const T = useChartT()
  const [open, setOpen] = React.useState(false)
  const violationCount = CHECK_KEYS.reduce((n, k) => n + (violations[k] ? 1 : 0), 0)
  const hasIssues = violationCount > 0 || dayNote.length > 0

  return (
    <div className="tw-rounded tw-border tw-border-slate-200 tw-bg-white">
      <button
        type="button"
        className="tw-flex tw-w-full tw-items-center tw-justify-between tw-px-3 tw-py-2 tw-text-left"
        onClick={() => setOpen((v) => !v)}
      >
        <span className="tw-text-xs tw-font-semibold tw-uppercase tw-text-slate-500">
          {T('discViolations', 'Błędy tarczki')}
          {violationCount > 0 && (
            <span className="tw-ml-2 tw-rounded-full tw-bg-red-100 tw-px-1.5 tw-py-0.5 tw-text-[10px] tw-font-bold tw-text-red-700">
              {violationCount}
            </span>
          )}
          {hasIssues && <span className="tw-ml-2 tw-text-red-600">!</span>}
        </span>
        <span className="tw-text-slate-400">{open ? '▾' : '▸'}</span>
      </button>
      {open && (
        <div className="tw-border-t tw-border-slate-200 tw-px-3 tw-py-2">
          <div className="tw-mb-3 tw-flex tw-flex-col tw-gap-1">
            {ROWS.map((row, i) => {
              if (row.kind === 'header') {
                return (
                  <p
                    key={`h-${i}`}
                    className="tw-mt-2 tw-text-xs tw-leading-snug tw-text-slate-700"
                  >
                    {T(row.tKey, row.label)}
                  </p>
                )
              }
              return (
                <label
                  key={row.key}
                  className={[
                    'tw-flex tw-cursor-pointer tw-items-start tw-gap-2 tw-text-xs tw-leading-snug tw-text-slate-700',
                    row.indent ? 'tw-ml-4' : '',
                  ].join(' ')}
                >
                  <input
                    type="checkbox"
                    className="tw-mt-0.5"
                    checked={violations[row.key]}
                    onChange={(e) => onChangeViolation(row.key, e.target.checked)}
                  />
                  <span>{T(row.tKey, row.label)}</span>
                </label>
              )
            })}
          </div>
          <label className="tw-block tw-text-xs tw-font-medium tw-text-slate-600">
            {T('note', 'Notatka')}
          </label>
          <textarea
            value={dayNote}
            onChange={(e) => onChangeDayNote(e.target.value)}
            rows={3}
            maxLength={DAY_NOTES_MAX_LENGTH}
            className="tw-w-full tw-rounded tw-border tw-border-slate-300 tw-px-2 tw-py-1 tw-text-sm"
          />
        </div>
      )}
    </div>
  )
}
